import classNames from 'classnames';
import React, { useCallback, useState } from 'react';
import { ConnectDragSource } from 'react-dnd';
import ActionIcon from 'components/ActionIcon';
import { IconType } from 'components/Icon/constants';
import ResizeButton from 'components/ResizeButton';
import { intlGet } from 'utils/intlGet';
import styles from './styles.module.scss';

type Props = {
  areActionsDisabled: boolean;
  connectDragSource: ConnectDragSource;
  deleteLayout: () => void;
  finishResize: () => void;
  resize: (change: DragEvent) => void;
  setMoveableAsset: (isDraggable: boolean) => void;
  spacerHeight: number;
  dataTestId?: string;
};

const SpacerActions: React.FC<Props> = ({
  spacerHeight,
  deleteLayout,
  connectDragSource,
  resize,
  finishResize,
  setMoveableAsset,
  areActionsDisabled,
  dataTestId = 'spacerActions',
}) => {
  const [show, setShow] = useState(false);
  const [isResizing, setIsResizing] = useState(false);

  const handleDragStart = useCallback(() => {
    setIsResizing(true);
  }, []);

  const handleDragStop = useCallback(() => {
    finishResize();
    setIsResizing(false);
    setShow(false);
  }, [finishResize]);

  const hideActions = (): void => {
    if (!isResizing) {
      setShow(false);
    }
  };

  const showActions = (): void => {
    if (!areActionsDisabled) {
      setShow(true);
    }
  };

  const enableLayoutMovement = (): void => {
    setMoveableAsset(true);
  };

  const disableLayoutMovement = (): void => {
    setMoveableAsset(false);
  };

  const opacityStyle = show ? 1 : 0;
  const hideHint = spacerHeight < 25;

  return (
    <div
      className={styles.SpacerActions}
      style={{ opacity: opacityStyle }}
      onMouseEnter={showActions}
      onMouseLeave={hideActions}
      data-testid={dataTestId}
    >
      <div
        className={classNames(styles.hint, { [styles.hidden]: hideHint })}
        data-testid={`${dataTestId}-hint`}
      >
        {intlGet('Artboard.Spacer.Text', 'Hint')}
      </div>
      {connectDragSource(
        <div
          className={styles.leftAction}
          onMouseDown={hideActions}
          onMouseUp={showActions}
          onMouseEnter={enableLayoutMovement}
          onMouseLeave={disableLayoutMovement}
          data-testid={`${dataTestId}-left`}
        >
          <ActionIcon
            type={IconType.UP_AND_DOWN_ARROWS}
            size="badge"
            color="secondary"
          />
        </div>,
      )}
      <div
        className={styles.rightAction}
        onClick={deleteLayout}
        data-testid={`${dataTestId}-right`}
      >
        <ActionIcon
          type={IconType.DELETE}
          size="xs"
          color="secondary"
        />
      </div>
      <div className={styles.bottomActionContainer} data-testid={`${dataTestId}-bottom`}>
        <ResizeButton
          handleDrag={resize}
          handleDragStart={handleDragStart}
          handleDragStop={handleDragStop}
          height={spacerHeight}
        />
      </div>
    </div>
  );
};

export default SpacerActions;
